import CdekLogo from "cdek.svg";
import cn from "classnames";
import { Datetime, Select } from "components/shared/form";
import {
    documentsTrackingStatuses,
    documentsTrackingStatusesEnum,
    parcelProviderTypes,
    parcelProviderTypesEnum,
    parcelRelationTypesEnum,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { enumLabel, ymEvent } from "helpers/system";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RussianPostLogo from "russian-post.svg";
import { applyBidPaymentDocumentsTracking } from "sources/books/bids/closeBids";
import { applyParcel } from "sources/parcel";
import BidParcelItem from "./item";

const TRACKING_MODE_AUTO = "auto";
const TRACKING_MODE_MANUAL = "manual";

const dateFields = ["plannedArrivalDate", "documentsReceiptDate"];

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

export default function BidParcels({ bid, modal, getItem, relationType, providers }) {
    const payment = bid.payment ?? {};

    const hasExistingManualData =
        !!payment.documentsTrackingStatus &&
        payment.documentsTrackingStatus !== documentsTrackingStatusesEnum.NotShipped;
    const defaultMode = !bid.parcels?.length && hasExistingManualData ? TRACKING_MODE_MANUAL : TRACKING_MODE_AUTO;

    const [trackingMode, setTrackingMode] = useState(defaultMode);
    const [provider, setProvider] = useState(providers?.length === 1 ? providers[0] : null);
    const [parcelData, setParcelData] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditingManual, setIsEditingManual] = useState(false);

    const [manualData, setManualData] = useState({
        documentsTrackingStatus: payment.documentsTrackingStatus || documentsTrackingStatusesEnum.NotShipped,
        plannedArrivalDate: formatDate(payment.plannedArrivalDate),
        documentsReceiptDate: formatDate(payment.documentsReceiptDate),
    });

    const isManual = trackingMode === TRACKING_MODE_MANUAL;

    const isInTransit = manualData.documentsTrackingStatus === documentsTrackingStatusesEnum.InTransit;
    const isDelivered = manualData.documentsTrackingStatus === documentsTrackingStatusesEnum.Delivered;
    const hasPlannedArrivalDate = isInTransit || isDelivered;

    const canSaveManual = useMemo(
        () => !loading && !!manualData.documentsTrackingStatus && (!isDelivered || !!manualData.documentsReceiptDate),
        [loading, manualData, isDelivered],
    );

    const apply = () => {
        ymEvent(
            relationType === parcelRelationTypesEnum.Bid
                ? "TrackingDocumentInTheBid"
                : "TrackingDocumentDistributionBid",
        );
        setLoading(true);
        applyParcel({
            entityId: bid.id,
            relationType,
            provider,
            externalId: parcelData.externalId,
        }).then((res) => {
            if (res) {
                getItem();
            }
            setLoading(false);
        });
    };

    const handleManualChange = useCallback(({ name, value }) => {
        setManualData((prev) => ({
            ...prev,
            [name]: dateFields.includes(name) ? formatDate(value) : value,
            ...(name === "documentsTrackingStatus" && value === documentsTrackingStatusesEnum.NotShipped
                ? { plannedArrivalDate: "", documentsReceiptDate: "" }
                : {}),
            ...(name === "documentsTrackingStatus" && value !== documentsTrackingStatusesEnum.Delivered
                ? { documentsReceiptDate: "" }
                : {}),
        }));
    }, []);

    const handleSaveManual = useCallback(() => {
        if (!canSaveManual) {
            return;
        }

        setLoading(true);
        applyBidPaymentDocumentsTracking({
            bidId: bid.id,
            documentsTrackingStatus: manualData.documentsTrackingStatus,
            plannedArrivalDate: manualData.plannedArrivalDate || null,
            documentsReceiptDate: manualData.documentsReceiptDate || null,
        }).then((response) => {
            if (response) {
                setIsEditingManual(false);
                getItem();
            }
            setLoading(false);
        });
    }, [bid.id, canSaveManual, getItem, manualData]);

    const handleModeChange = (mode) => {
        setTrackingMode(mode);
        if (mode === TRACKING_MODE_MANUAL) {
            setIsEditingManual(!hasExistingManualData);
        }
    };

    const manualStatusLabel = enumLabel(documentsTrackingStatuses, manualData.documentsTrackingStatus);

    return (
        <>
            <div className="show-bid__section-header d-flex align-items-center justify-content-between">
                <span>Отслеживание документа</span>
                {relationType === parcelRelationTypesEnum.Bid && (
                    <Link className="small font-weight-normal" to={paths.documentTracking}>
                        Отчет по отслеживанию
                    </Link>
                )}
            </div>
            <div className={cn("show-bid__section py-2", { loading })}>
                <div className="mb-3">
                    <div className="text-muted small mb-2">Способ отслеживания</div>
                    <div className="row no-gutters" style={{ gap: "8px" }}>
                        <div className="col">
                            <div
                                className={cn("parcel__mode-card", {
                                    "parcel__mode-card--active": trackingMode === TRACKING_MODE_AUTO,
                                })}
                                onClick={() => handleModeChange(TRACKING_MODE_AUTO)}
                            >
                                <div className="d-flex align-items-start">
                                    <div
                                        className={cn("parcel__mode-radio", {
                                            "parcel__mode-radio--active": trackingMode === TRACKING_MODE_AUTO,
                                        })}
                                    />
                                    <div className="ml-2">
                                        <div className="font-weight-medium">Автоматическое (по трек-коду)</div>
                                        <div className="text-muted small">
                                            Отслеживание через интеграцию с перевозчиком
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col">
                            <div
                                className={cn("parcel__mode-card", {
                                    "parcel__mode-card--active": trackingMode === TRACKING_MODE_MANUAL,
                                })}
                                onClick={() => handleModeChange(TRACKING_MODE_MANUAL)}
                            >
                                <div className="d-flex align-items-start">
                                    <div
                                        className={cn("parcel__mode-radio", {
                                            "parcel__mode-radio--active": trackingMode === TRACKING_MODE_MANUAL,
                                        })}
                                    />
                                    <div className="ml-2">
                                        <div className="font-weight-medium">Ручное</div>
                                        <div className="text-muted small">Статус и даты указываются вручную</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {isManual ? (
                    <div>
                        {isEditingManual ? (
                            <div>
                                <div className="row">
                                    <div className="col-12">
                                        <Select
                                            labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                                            fieldWrapper="col-12"
                                            label="Статус отслеживания документов"
                                            inputProps={{ name: "documentsTrackingStatus" }}
                                            options={documentsTrackingStatuses}
                                            value={manualData.documentsTrackingStatus}
                                            onChange={handleManualChange}
                                            menuPortalTarget={document.body}
                                            unClearable
                                        />
                                    </div>
                                    {hasPlannedArrivalDate && (
                                        <div className="col-6">
                                            <Datetime
                                                labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                                                wrapper="col-12"
                                                label="Плановая дата прибытия"
                                                inputProps={{ name: "plannedArrivalDate", type: "date" }}
                                                value={manualData.plannedArrivalDate}
                                                onChange={handleManualChange}
                                            />
                                        </div>
                                    )}
                                    {isDelivered && (
                                        <div className="col-6">
                                            <Datetime
                                                labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                                                wrapper="col-12"
                                                label="Дата получения"
                                                inputProps={{ name: "documentsReceiptDate", type: "date" }}
                                                value={manualData.documentsReceiptDate}
                                                onChange={handleManualChange}
                                                required
                                            />
                                        </div>
                                    )}
                                </div>
                                {isInTransit && !isDelivered && (
                                    <div className="alert alert-info py-2 px-3 small mb-3">
                                        Укажите плановую дату прибытия документов.
                                        <br />
                                        Вы сможете обновить статус и дату получения после фактического получения.
                                    </div>
                                )}
                                <div className="d-flex">
                                    {hasExistingManualData && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary mr-2"
                                            onClick={() => setIsEditingManual(false)}
                                        >
                                            Отменить
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-brand"
                                        onClick={handleSaveManual}
                                        disabled={!canSaveManual}
                                    >
                                        Сохранить
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="row mb-2">
                                    <div className="col-6">
                                        <div className="text-muted small mb-1">Статус отслеживания документов</div>
                                        <div>{manualStatusLabel || "—"}</div>
                                    </div>
                                    {payment.plannedArrivalDate && (
                                        <div className="col-6">
                                            <div className="text-muted small mb-1">Плановая дата прибытия</div>
                                            <div>{moment(payment.plannedArrivalDate).format("DD.MM.YYYY HH:mm")}</div>
                                        </div>
                                    )}
                                    {payment.documentsReceiptDate && (
                                        <div className="col-6 mt-2">
                                            <div className="text-muted small mb-1">Дата получения</div>
                                            <div>{moment(payment.documentsReceiptDate).format("DD.MM.YYYY HH:mm")}</div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setIsEditingManual(true)}
                                >
                                    <i className="icon-pencil mr-1" />
                                    Редактировать
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="parcel__items">
                            <div>Выберите систему отслеживания:</div>
                            {(providers.includes(parcelProviderTypesEnum.RussianPost) ||
                                provider === parcelProviderTypesEnum.RussianPost) && (
                                <div
                                    onClick={() =>
                                        setProvider(
                                            provider === parcelProviderTypesEnum.RussianPost
                                                ? null
                                                : parcelProviderTypesEnum.RussianPost,
                                        )
                                    }
                                    className={cn("parcel__picker", {
                                        "parcel__picker--selected": provider === parcelProviderTypesEnum.RussianPost,
                                        "parcel__picker--unselected":
                                            provider && provider !== parcelProviderTypesEnum.RussianPost,
                                    })}
                                >
                                    <RussianPostLogo className="parcel__logo" />
                                </div>
                            )}
                            {(providers.includes(parcelProviderTypesEnum.Cdek) ||
                                provider === parcelProviderTypesEnum.Cdek) && (
                                <div
                                    onClick={() =>
                                        setProvider(
                                            provider === parcelProviderTypesEnum.Cdek
                                                ? null
                                                : parcelProviderTypesEnum.Cdek,
                                        )
                                    }
                                    className={cn("parcel__picker", {
                                        "parcel__picker--selected": provider === parcelProviderTypesEnum.Cdek,
                                        "parcel__picker--unselected":
                                            provider && provider !== parcelProviderTypesEnum.Cdek,
                                    })}
                                >
                                    <CdekLogo className="parcel__logo" />
                                </div>
                            )}
                        </div>
                        <div className="mb-3">
                            <div className="input-group">
                                <input
                                    disabled={!provider || parcelData.id}
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Введите трек-код, например: LA0942127883SE"
                                    value={parcelData.externalId}
                                    onChange={(e) => setParcelData({ ...parcelData, externalId: e.target.value })}
                                />
                                <div className="input-group-append">
                                    <button
                                        className="btn btn-sm btn-brand"
                                        type="button"
                                        onClick={apply}
                                        disabled={!provider || parcelData.id}
                                    >
                                        Отследить
                                    </button>
                                </div>
                            </div>
                        </div>
                        {provider && !parcelData.id && (
                            <div className="mb-2">
                                <div className="pb-3">
                                    Отслеживание осуществляется только по компании{" "}
                                    <b>{enumLabel(parcelProviderTypes, provider)}</b>
                                </div>
                                Пример: LA09421212784SE, EN356876543JP, LP987654321098 Для того, чтобы{" "}
                                <b>отследить посылку</b>, укажите полученный трек-код, кликните по кнопке "Отследить".
                            </div>
                        )}
                        {bid.parcels.map((item) => (
                            <BidParcelItem
                                key={item.id}
                                bid={bid}
                                parcel={item}
                                modal={modal}
                                getItem={getItem}
                                relationType={relationType}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
